import { useMemo, useState } from 'react';
import ListView from 'src/layouts/ListView';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  fetchWorkspaceTags,
  fetchWorkspaceSubmissions,
  IWorkspaceSubmissionFilter,
  fetchOrganization
} from 'src/api/queries';
import { useCloneSubmission } from 'src/api/commands';
import { useWorkspaceData } from 'src/contexts/WorkspaceProvider';
import { ISubmissionWorkflowState } from '@publishing-engine/domain/models';
import Footer from '../../../../../components/Footer';
import ScrollToTopButton from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import CloneSubmissionModal from './CloneSubmissionModal';
import CloneSubmissionSuccessState from './CloneSubmissionSuccessState';
import RejectedSubmissions from './RejectedSubmissions';
import AcceptedSubmissions from './AcceptedSubmissions';
import WithdrawnSubmissions from './WithdrawnSubmissions';
import { useToast } from '@morressier/alembic';
import AllArchivedSubmissions from './AllArchivedSubmissions';

const DECISION_TABS = {
  ALL: 'all',
  ACCEPTED_FOR_PUBLICATION: 'acceptedForPublication',
  REJECTED: 'rejected',
  WITHDRAWN: 'withdrawn'
} as const;

type DecisionTab = (typeof DECISION_TABS)[keyof typeof DECISION_TABS];

const TAB_LABEL_MAP: Record<DecisionTab, string> = {
  [DECISION_TABS.ALL]: 'All',
  [DECISION_TABS.ACCEPTED_FOR_PUBLICATION]: 'Accepted for Publication',
  [DECISION_TABS.REJECTED]: 'Rejected',
  [DECISION_TABS.WITHDRAWN]: 'Withdrawn'
};

const TAB_WORKFLOW_PHASE_MAP: Record<DecisionTab, ISubmissionWorkflowState[]> =
  {
    [DECISION_TABS.ALL]: [
      'accepted',
      'formalAcceptance',
      'rejected',
      'withdrawn'
    ],
    [DECISION_TABS.ACCEPTED_FOR_PUBLICATION]: ['accepted', 'formalAcceptance'],
    [DECISION_TABS.REJECTED]: ['rejected'],
    [DECISION_TABS.WITHDRAWN]: ['withdrawn']
  };

const DECISION_TAB_VALUES: DecisionTab[] = Object.values(DECISION_TABS);

const defaultValues: IWorkspaceSubmissionFilter = {
  workflowPhase: [],
  journalId: [],
  documentType: [],
  assigneeId: [],
  tagId: [],
  searchParams: '',
  focusIssueId: []
};

type SelectedSubmissionForClone = {
  manuscriptId: string;
  sourceSubmissionId: string;
  submissionContextId: string;
};

type CloneSuccessData = {
  submissionId: string;
  manuscriptId: string;
  submissionContextId: string;
};

const JournalArchive = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { shortname = '' } = useParams();
  const { workspace } = useWorkspaceData();
  const workspaceId = workspace.id;

  // Read active tab from URL on mount, default to rejected
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get('activeTab');
  const isValidTab = (t: string | null): t is DecisionTab =>
    DECISION_TAB_VALUES.includes(t as DecisionTab);

  const [activeTab, setActiveTab] = useState<DecisionTab>(
    isValidTab(tabFromUrl) ? tabFromUrl : DECISION_TABS.REJECTED
  );

  const handleTabChange = (tab: DecisionTab) => {
    if (tab === activeTab) {
      return;
    }

    setActiveTab(tab);
    // Reset filters when switching tabs: keep only the activeTab param so the
    // newly mounted tab starts with clean filters instead of rehydrating the
    // previous tab's filters from the URL.
    navigate(`${location.pathname}?activeTab=${tab}`, { replace: true });
  };

  const [selectedSubmissionForClone, setSelectedSubmissionForClone] =
    useState<SelectedSubmissionForClone | null>(null);
  const [cloneSuccessData, setCloneSuccessData] =
    useState<CloneSuccessData | null>(null);

  const { watch } = useForm<IWorkspaceSubmissionFilter>({ defaultValues });
  const filters = watch();

  const { data: manageTags } = useQuery({
    queryKey: ['workspace-tags', { workspaceId }],
    queryFn: () => (workspaceId ? fetchWorkspaceTags(workspaceId) : null)
  });

  const countFilters = useMemo(() => {
    const userTags = filters.tagId.filter(tagId =>
      manageTags?.collection.some(
        allTag => tagId === allTag.id && allTag.usage === 'users'
      )
    );
    const submissionTags = filters.tagId.filter(tagId =>
      manageTags?.collection.some(
        allTag => tagId === allTag.id && allTag.usage === 'submissions'
      )
    );
    return {
      ...filters,
      tagId: submissionTags ?? [],
      userTags: userTags ?? [],
      workflowPhase: []
    };
  }, [filters, manageTags?.collection]);

  const { data: rejectedCountData } = useQuery({
    queryKey: [
      'submission-list-archive-count',
      { workspaceId, tab: DECISION_TABS.REJECTED, countFilters }
    ],
    enabled: !!workspaceId,
    queryFn: () =>
      fetchWorkspaceSubmissions({
        workspaceId,
        limit: 1,
        offset: 0,
        filters: {
          ...countFilters,
          workflowPhase: TAB_WORKFLOW_PHASE_MAP[DECISION_TABS.REJECTED] as any
        }
      })
  });

  const { data: withdrawnCountData } = useQuery({
    queryKey: [
      'submission-list-archive-count',
      { workspaceId, tab: DECISION_TABS.WITHDRAWN, countFilters }
    ],
    enabled: !!workspaceId,
    queryFn: () =>
      fetchWorkspaceSubmissions({
        workspaceId,
        limit: 1,
        offset: 0,
        filters: {
          ...countFilters,
          workflowPhase: TAB_WORKFLOW_PHASE_MAP[DECISION_TABS.WITHDRAWN] as any
        }
      })
  });

  const { data: allCountData } = useQuery({
    queryKey: [
      'submission-list-all-count',
      { workspaceId, tab: DECISION_TABS.ALL, countFilters }
    ],
    enabled: !!workspaceId,
    queryFn: () =>
      fetchWorkspaceSubmissions({
        workspaceId,
        limit: 1,
        offset: 0,
        filters: {
          ...countFilters,
          workflowPhase: TAB_WORKFLOW_PHASE_MAP[DECISION_TABS.ALL] as any
        }
      })
  });
  const { data: acceptedCountData } = useQuery({
    queryKey: [
      'submission-list-archive-count',
      {
        workspaceId,
        tab: DECISION_TABS.ACCEPTED_FOR_PUBLICATION,
        countFilters
      }
    ],
    enabled: !!workspaceId,
    queryFn: () =>
      fetchWorkspaceSubmissions({
        workspaceId,
        limit: 1,
        offset: 0,
        filters: {
          ...countFilters,
          workflowPhase: TAB_WORKFLOW_PHASE_MAP[
            DECISION_TABS.ACCEPTED_FOR_PUBLICATION
          ] as any
        }
      })
  });

  const TAB_COUNT_MAP: Record<DecisionTab, number> = {
    [DECISION_TABS.ALL]: allCountData?.total ?? 0,
    [DECISION_TABS.ACCEPTED_FOR_PUBLICATION]: acceptedCountData?.total ?? 0,
    [DECISION_TABS.REJECTED]: rejectedCountData?.total ?? 0,
    [DECISION_TABS.WITHDRAWN]: withdrawnCountData?.total ?? 0
  };

  const getTabLabel = (tab: DecisionTab) => {
    const count = TAB_COUNT_MAP[tab];
    return count !== undefined
      ? `${TAB_LABEL_MAP[tab]} (${count})`
      : TAB_LABEL_MAP[tab];
  };

  const { data: organization } = useQuery({
    queryKey: [`organization-${shortname}`],
    queryFn: () => (shortname ? fetchOrganization(shortname) : null),
    retry: 0
  });
  const organizationId = organization?.id ?? '';

  const cloneSubmissionMutation = useCloneSubmission(
    selectedSubmissionForClone?.sourceSubmissionId ?? '',
    selectedSubmissionForClone?.submissionContextId ?? '',
    () => {
      queryClient.invalidateQueries({
        queryKey: ['submission-list-archive']
      });
      queryClient.invalidateQueries({
        queryKey: ['submission-list']
      });
    },
    organizationId
  );

  const handleCloneSubmission = async () => {
    const response = await cloneSubmissionMutation.mutateAsync();

    if (!response.isSuccess) {
      const message =
        response?.message?.errors?.[0]?.reason ||
        response?.message?.message ||
        'Failed to clone submission';

      toast({
        variant: 'error',
        title: 'Failed to clone submission',
        description: message,
        isClosable: true
      });
      return;
    }

    setCloneSuccessData({
      submissionId: response.submissionId,
      manuscriptId:
        response.manuscriptId ?? selectedSubmissionForClone?.manuscriptId ?? '',
      submissionContextId: selectedSubmissionForClone?.submissionContextId ?? ''
    });
    setSelectedSubmissionForClone(null);
  };

  const renderTab = (tab: DecisionTab) => {
    switch (tab) {
      case DECISION_TABS.ALL:
        return <AllArchivedSubmissions workspace={workspace} setSelectedSubmissionForClone={setSelectedSubmissionForClone} />;
      case DECISION_TABS.ACCEPTED_FOR_PUBLICATION:
        return <AcceptedSubmissions workspace={workspace} setSelectedSubmissionForClone={setSelectedSubmissionForClone} />;
      case DECISION_TABS.REJECTED:
        return <RejectedSubmissions workspace={workspace} setSelectedSubmissionForClone={setSelectedSubmissionForClone} />;
      case DECISION_TABS.WITHDRAWN:
        return <WithdrawnSubmissions workspace={workspace} setSelectedSubmissionForClone={setSelectedSubmissionForClone} />;
      default:
        return null;
    }
  };

  return (
    <Box id="scrollable-wrapper" overflowY="auto" height="100%">
      <Box height="100%">
        {cloneSuccessData ? (
          <CloneSubmissionSuccessState
            shortname={shortname ?? ''}
            submissionId={cloneSuccessData.submissionId}
            manuscriptId={cloneSuccessData.manuscriptId}
            submissionContextId={cloneSuccessData.submissionContextId}
            onBackToArchive={() => setCloneSuccessData(null)}
          />
        ) : (
          <>
            {selectedSubmissionForClone && (
              <CloneSubmissionModal
                manuscriptId={selectedSubmissionForClone.manuscriptId}
                isLoading={cloneSubmissionMutation.isLoading}
                onClose={() => setSelectedSubmissionForClone(null)}
                onConfirm={handleCloneSubmission}
              />
            )}
            <Box padding={5} minHeight="100%">
              <Flex
                flexDirection="column"
                width="100%"
                alignItems="flex-start"
                gap={4}
              >
                <Flex
                  gap={2}
                  alignItems="center"
                  flexWrap={{ base: 'nowrap', md: 'wrap' }}
                  overflowX={{ base: 'auto', md: 'visible' }}
                  borderBottom="1px solid"
                  borderColor="neutralPrimaryBorder"
                  mb={2}
                  width="100%"
                >
                  {DECISION_TAB_VALUES.map(tab => (
                    <ListView.Tab
                      key={tab}
                      onClick={() => handleTabChange(tab)}
                      isActive={activeTab === tab}
                    >
                      <Text textStyle="headingLine">{getTabLabel(tab)}</Text>
                    </ListView.Tab>
                  ))}
                </Flex>

                {renderTab(activeTab)}
              </Flex>
            </Box>
            <Footer />
          </>
        )}
      </Box>
      <ScrollToTopButton />
    </Box>
  );
};

export default JournalArchive;
